import { Children, Fragment, isValidElement } from 'react'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import * as SelectPrimitive from '@radix-ui/react-select'

const EMPTY_VALUE = '__shadcn_empty_value__'

function optionText(children) {
  return Children.toArray(children).map((child) => typeof child === 'string' || typeof child === 'number' ? String(child) : '').join('').trim()
}

function collectOptions(children, result = []) {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return
    if (child.type === 'option') {
      const label = optionText(child.props.children)
      const rawValue = child.props.value ?? label
      result.push({ value: String(rawValue), label, disabled: Boolean(child.props.disabled) })
      return
    }
    if (child.type === Fragment || child.type === 'optgroup') collectOptions(child.props.children, result)
  })
  return result
}

function normalizedValue(value) {
  return String(value ?? '') === '' ? EMPTY_VALUE : String(value)
}

export function ShadcnSelect({ value, defaultValue, onChange, onValueChange, children, className = '', disabled = false, required = false, name, id, 'aria-label': ariaLabel, 'aria-invalid': ariaInvalid }) {
  const options = collectOptions(children)
  const uniqueOptions = options.filter((option, index) => options.findIndex((candidate) => candidate.value === option.value) === index)
  const currentValue = value === undefined ? undefined : normalizedValue(value)
  const initialValue = defaultValue === undefined ? undefined : normalizedValue(defaultValue)
  const change = (nextValue) => {
    const normalized = nextValue === EMPTY_VALUE ? '' : nextValue
    onValueChange?.(normalized)
    onChange?.({ target: { value: normalized, name } })
  }
  return <SelectPrimitive.Root value={currentValue} defaultValue={initialValue} onValueChange={change} disabled={disabled} required={required} name={name}>
    <SelectPrimitive.Trigger id={id} aria-label={ariaLabel} aria-invalid={ariaInvalid} className={`shadcn-select-trigger ${className}`.trim()}>
      <SelectPrimitive.Value/>
      <SelectPrimitive.Icon className="shadcn-select-icon"><ChevronDown/></SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content className="shadcn-select-content" position="popper" sideOffset={5} collisionPadding={10}>
        <SelectPrimitive.ScrollUpButton className="shadcn-select-scroll"><ChevronUp/></SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport className="shadcn-select-viewport">
          {uniqueOptions.map((option) => <SelectPrimitive.Item className="shadcn-select-item" value={option.value === '' ? EMPTY_VALUE : option.value} disabled={option.disabled} key={`${option.value}-${option.label}`}>
            <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
            <SelectPrimitive.ItemIndicator className="shadcn-select-check"><Check/></SelectPrimitive.ItemIndicator>
          </SelectPrimitive.Item>)}
        </SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="shadcn-select-scroll"><ChevronDown/></SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  </SelectPrimitive.Root>
}
